import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  BookMarked, ArrowLeft, ChevronRight, Loader2, CheckCircle, 
  BookOpen, GraduationCap, Send, Eye, EyeOff 
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = window.location.origin + "/api";

// Class configuration
const CLASS_CONFIG = {
  tgt: [
    { class: "6", label: "Class 6th", color: "from-red-500 to-pink-500", icon: "6" },
    { class: "7", label: "Class 7th", color: "from-orange-500 to-amber-500", icon: "7" },
    { class: "8", label: "Class 8th", color: "from-yellow-500 to-lime-500", icon: "8" },
    { class: "9", label: "Class 9th", color: "from-green-500 to-emerald-500", icon: "9" },
    { class: "10", label: "Class 10th", color: "from-teal-500 to-cyan-500", icon: "10" },
  ],
  pgt: [
    { class: "9", label: "Class 9th", color: "from-green-500 to-emerald-500", icon: "9" },
    { class: "10", label: "Class 10th", color: "from-teal-500 to-cyan-500", icon: "10" },
    { class: "11", label: "Class 11th", color: "from-blue-500 to-indigo-500", icon: "11" },
    { class: "12", label: "Class 12th", color: "from-purple-500 to-violet-500", icon: "12" },
  ],
};

export default function NCERTExemplar({ user, subjectSelection }) {
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [showModelAnswer, setShowModelAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const examType = subjectSelection?.category || "tgt";
  const subject = subjectSelection?.subject || "";
  const subjectLabel = subjectSelection?.subjectLabel || "";
  const classes = CLASS_CONFIG[examType] || [];

  // Fetch stats on mount
  useEffect(() => {
    if (examType && subject) {
      fetchStats();
    }
  }, [examType, subject]);

  // Fetch questions when class is selected
  useEffect(() => {
    if (selectedClass) {
      fetchQuestions(selectedClass.class);
    }
  }, [selectedClass]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/notes/ncert/stats/${examType}/${subject}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const fetchQuestions = async (classLevel) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/notes/ncert/questions?exam_type=${examType}&subject=${subject}&class_level=${classLevel}`
      );
      setQuestions(res.data.questions || []);
    } catch (err) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error("Please write your answer");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/notes/ncert/attempt`, {
        user_id: user?.id || "guest",
        question_id: selectedQuestion.id,
        answer: userAnswer,
      });

      if (res.data.success) {
        toast.success("Answer submitted! Check the model answer below.");
        setShowModelAnswer(true);
      }
    } catch (err) {
      toast.error("Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  };

  // Question Detail View
  if (selectedQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8" data-testid="ncert-question-detail">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedQuestion(null);
              setUserAnswer("");
              setShowModelAnswer(false);
            }}
            className="mb-6 hover:bg-orange-100"
            data-testid="back-to-questions-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Questions
          </Button>

          {/* Question Card */}
          <Card className="shadow-xl border-0" data-testid="question-card">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-lg">
              <div className="flex justify-between items-center">
                <Badge className="bg-white/20 text-white" data-testid="chapter-badge">
                  {selectedQuestion.chapter}
                </Badge>
                <Badge className="bg-white text-orange-600" data-testid="marks-badge">
                  {selectedQuestion.marks} Marks
                </Badge>
              </div>
              {/* Question Text - Bilingual Support */}
              <CardTitle className="text-lg mt-4" data-testid="question-text">
                {selectedQuestion.question_text_english ? (
                  <div className="space-y-4">
                    <div>
                      <span className="text-xs bg-blue-500/30 px-2 py-1 rounded mb-2 inline-block">English</span>
                      <p className="mt-1">{selectedQuestion.question_text_english}</p>
                    </div>
                    {selectedQuestion.question_text_hindi && (
                      <div className="border-t border-white/20 pt-3">
                        <span className="text-xs bg-orange-500/30 px-2 py-1 rounded mb-2 inline-block">हिंदी</span>
                        <p className="mt-1">{selectedQuestion.question_text_hindi}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{selectedQuestion.question_text}</p>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Answer Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Answer / आपका उत्तर:
                </label>
                <Textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Write your detailed answer here... / अपना विस्तृत उत्तर यहाँ लिखें..."
                  className="min-h-[200px] text-base"
                  data-testid="answer-textarea"
                />
              </div>


              {/* Submit Button */}
              <Button
                onClick={handleSubmitAnswer}
                disabled={submitting || !userAnswer.trim()}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                data-testid="submit-answer-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Answer
                  </>
                )}
              </Button>

              {/* Model Answer - Bilingual Support */}
              {showModelAnswer && (selectedQuestion.answer || selectedQuestion.answer_english) && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="model-answer-section">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Model Answer / मॉडल उत्तर:</span>
                  </div>
                  {selectedQuestion.answer_english ? (
                    <div className="space-y-4">
                      <div>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mb-2 inline-block">English</span>
                        <p className="text-gray-700 whitespace-pre-wrap mt-1">{selectedQuestion.answer_english}</p>
                      </div>
                      {selectedQuestion.answer_hindi && (
                        <div className="border-t border-green-200 pt-3">
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded mb-2 inline-block">हिंदी</span>
                          <p className="text-gray-700 whitespace-pre-wrap mt-1">{selectedQuestion.answer_hindi}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedQuestion.answer}</p>
                  )}
                </div>
              )}

              {/* Toggle Model Answer (if not submitted) */}
              {!showModelAnswer && (selectedQuestion.answer || selectedQuestion.answer_english) && (
                <Button
                  variant="outline"
                  onClick={() => setShowModelAnswer(true)}
                  className="w-full"
                  data-testid="show-model-answer-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Show Model Answer / उत्तर देखें
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Questions List View
  if (selectedClass) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8" data-testid="ncert-questions-list">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => setSelectedClass(null)}
            className="mb-6 hover:bg-orange-100"
            data-testid="back-to-classes-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Classes
          </Button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${selectedClass.color} flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-4`}>
              {selectedClass.icon}
            </div>
            <h1 className="text-2xl font-bold text-gray-800" data-testid="class-subject-title">
              {selectedClass.label} - {subjectLabel}
            </h1>
            <p className="text-gray-600 mt-2">NCERT Exemplar Questions</p>
          </div>

          {/* Questions */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20" data-testid="loading-state">
              <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
              <p className="mt-4 text-gray-600">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <Card className="text-center py-16" data-testid="empty-state">
              <BookMarked className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">No Questions Yet</h3>
              <p className="text-gray-500 mt-2">
                Questions for this class will be added soon
              </p>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="questions-grid">
              {questions.map((q, idx) => (
                <Card
                  key={q.id || idx}
                  className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-orange-500"
                  onClick={() => setSelectedQuestion(q)}
                  data-testid={`question-card-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        {q.chapter}
                      </Badge>
                      <Badge className="bg-orange-100 text-orange-700">
                        {q.marks} Marks
                      </Badge>
                    </div>
                    <p className="text-gray-800 line-clamp-2">{q.question_text}</p>
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-xs text-gray-500 capitalize">{q.question_type}</span>
                      <span className="text-sm text-orange-600 font-medium flex items-center">
                        Attempt <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Class Selection View (Main)
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8" data-testid="ncert-class-selection">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-6 hover:bg-orange-100"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg mb-4">
            <BookMarked className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800" data-testid="ncert-title">NCERT Exemplar</h1>
          <p className="text-gray-600 mt-2" data-testid="subject-label">{subjectLabel}</p>
          <Badge className="mt-3 bg-orange-100 text-orange-700" data-testid="exam-type-badge">
            {examType.toUpperCase()} - {examType === 'tgt' ? 'Class 6-10' : 'Class 9-12'}
          </Badge>
        </div>

        {/* Select Class Text */}
        <h2 className="text-xl font-semibold text-gray-700 mb-6 text-center">
          Select Class to Practice
        </h2>

        {/* Class Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10" data-testid="class-grid">
          {classes.map((cls) => {
            const classStats = stats?.classes?.[cls.class];
            return (
              <Card
                key={cls.class}
                className="hover:shadow-xl transition-all cursor-pointer hover:scale-105 border-2 border-transparent hover:border-orange-400"
                onClick={() => setSelectedClass(cls)}
                data-testid={`class-card-${cls.class}`}
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 mx-auto rounded-xl bg-gradient-to-br ${cls.color} flex items-center justify-center text-white text-2xl font-bold shadow-md mb-3`}>
                    {cls.icon}
                  </div>
                  <h3 className="font-semibold text-gray-800">{cls.label}</h3>
                  {classStats && (
                    <Badge className="mt-2 bg-orange-100 text-orange-700" data-testid={`class-${cls.class}-count`}>
                      {classStats.question_count} Questions
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="bg-gradient-to-r from-orange-100 to-amber-100 border-orange-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              About NCERT Exemplar
            </h3>
            <ul className="space-y-2 text-sm text-orange-900">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Subjective questions from NCERT Exemplar books
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Chapter-wise organized for easy practice
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Model answers to compare your responses
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Track your progress and improve
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
